/* eslint-disable object-shorthand */
import React from 'react';
import { Panel, PanelType, Stack } from 'office-ui-fabric-react';
import { Field, Form, Formik } from 'formik';
import { useBoolean } from '@uifabric/react-hooks';
import { useDispatch, useSelector } from 'react-redux';
import { get, merge } from 'lodash';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Text } from 'office-ui-fabric-react/lib/Text';
import moment from 'moment';
import StackGrid from '../StackGrid';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import SubModuleContent from '../SubModuleContent';
import TextField from '../TextField';
import TextButton from '../TextButton';
import styles from './AdvanceGroupForm.style';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import ContractTypeAhead from '../../components/ContractTypeAhead';
import { createAdvanceGroup, editAdvanceGroup } from '../../actions/advanceGroups';
import GroupAssignmentsForm from '../GroupAssignmentsForm';
import { rowTransformer } from '../DynamicDetailsList/RowTransformation';
import {
  defaultTransformer,
  productTextTransformer,
  purchaseOriginTextTransformer,
  textTransformer,
} from '../DynamicDetailsList/ContractTransactionColumns';
import AdvanceGroupAssignmentDetails from '../AdvanceGroupAssignmentDetails';

const advanceGroupValidationSchema = Yup.object().shape({
  name: Yup.string().max(100).required('Required').label('Name'),
  contract: Yup.object({
    contractId: Yup.string().required('Required'),
  }),
  accountId: Yup.string().required('Required'),
  groupAssignments: Yup.object({
    dimensions: Yup.array(),
    filters: Yup.array(
      Yup.object().shape({
        recoupmentParams: Yup.array(),
      }),
    ),
  }),
});

const AdvanceGroupForm = React.forwardRef(
  (
    {
      advanceGroupId,
      onSubmit,
      onSave,
      onSuccess,
      onFailure,
      mode,
      initialContract,
      advanceGroupToEdit,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
    const advanceGroup =
      useSelector(state => get(state, `entities.advancegroups.${advanceGroupId}`, null)) ||
      advanceGroupToEdit;

    const initialValues = merge(
      {
        name: '',
        contract: {
          name: initialContract?.name || '',
          contractId: initialContract?.contractId || '',
        },
        accountId: '',
        groupAssignments: {
          dimensions: [],
          filters: [
            {
              _id: '0',
              key: '0',
              type: 'include',
              effectiveDate:
                initialContract?.effectiveDate || moment.utc().startOf('day').toISOString(),
              terminationDate:
                initialContract?.terminationDate ||
                moment.utc('2099-12-31').startOf('day').toISOString(),
              recoupmentParams: [{ property: 'recoupmentPercentage', value: 100 }],
            },
          ],
        },
        submitButtonType: '',
      },
      advanceGroup,
    );

    const transformFormFiltersToTextFormat = formData => {
      // override default transformer functions to return text to be rendered in details list
      const dimensionColumns = formData?.dimensions?.map(dimension => {
        if (dimension.key === 'product') {
          return {
            ...dimension,
            transformer: productTextTransformer,
          };
        }
        if (dimension.key === 'purchaseOrigin') {
          return {
            ...dimension,
            transformer: purchaseOriginTextTransformer,
          };
        }
        return {
          ...dimension,
          transformer: textTransformer,
        };
      });

      const filters = formData.filters.map(
        rowTransformer(
          { ...dimensionColumns },
          defaultTransformer,
          '_id',
          'effectiveDate',
          'terminationDate',
          'type',
          'recoupmentParams',
        ),
      );

      return filters;
    };

    const handleSaveGroupAssignment = (formData, setFieldValue, setFieldTouched) => {
      const dimensions = formData?.dimensions?.map(dimension => {
        return dimension.key;
      });

      const filters = transformFormFiltersToTextFormat(formData);
      const formattedFormData = { dimensions, filters };

      setFieldValue('groupAssignments', formattedFormData);
      setFieldTouched('groupAssignments');
      dismissPanel();
    };

    const submitHandler = async (values, { setErrors }) => {
      let response;

      // POST form data to api
      const formValues = {
        name: values.name,
        contractId: values.contract.contractId,
        accountId: values.accountId,
        groupAssignments: values.groupAssignments,
      };

      switch (values.submitButtonType) {
        case 'Save':
          switch (mode) {
            case AdvanceGroupForm.MODE.CREATE:
              response = await dispatch(createAdvanceGroup(formValues));
              break;
            case AdvanceGroupForm.MODE.EDIT:
              response = await dispatch(editAdvanceGroup(formValues, values._id));
              break;
            default:
              break;
          }
          break;
        default:
          return {};
      }

      // Display error messages from api , if any, on the UI
      if (response.error && response.payload.message) {
        const error = new Error(response.payload.message);
        error.payload = response.payload.message;
        setErrors({ form: error.message });
        await onFailure(response);
        return response;
      }

      // Get the response and pass it back to onSuccess callback
      response = get(
        response,
        `payload.entities.advancegroups.${get(response, 'payload.result')}`,
        {},
      );
      await onSuccess(response);

      return response;
    };

    const contractChangeCallback = (contract, errors, setFieldValue, setFieldTouched, values) => {
      if (values.groupAssignments?.filters?.length === 1) {
        const effectiveDate = contract?.effectiveDate
          ? contract?.effectiveDate
          : moment.utc().startOf('day').toISOString();
        setFieldValue('groupAssignments.filters[0].effectiveDate', effectiveDate);
        setFieldTouched('groupAssignments.filters[0].effectiveDate');

        const terminationDate = contract?.terminationDate
          ? contract?.terminationDate
          : moment.utc('2099-12-31').startOf('day').toDate();
        setFieldValue('groupAssignments.filters[0].terminationDate', terminationDate);
        setFieldTouched('groupAssignments.filters[0].terminationDate');

        // eslint-disable-next-line no-param-reassign
        delete errors.groupAssignments;
      }
    };

    return (
      <Formik
        innerRef={ref}
        initialValues={initialValues}
        enableReinitialize={false}
        validationSchema={advanceGroupValidationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmit || onSave || submitHandler}
      >
        {({ errors, isSubmitting, values, setFieldValue, setFieldTouched }) => (
          <Form>
            {errors.form && (
              <MessageBar
                messageBarType={MessageBarType.error}
                messageBarIconProps={{ iconName: 'Info' }}
                isMultiline
                dismissButtonAriaLabel="Close"
                nowrap
              >
                {errors.form}
              </MessageBar>
            )}

            <Module>
              <ModuleContent>
                <SubModule disableAccordian>
                  <SubModuleHeader variant="large">Advance Group Details</SubModuleHeader>

                  <SubModuleContent>
                    <StackGrid smCols={1} mdCols={2} lgCols={2} shrink>
                      <Field
                        name="name"
                        label="Advance Group Name"
                        component={TextField}
                        disabled={isSubmitting}
                        callout="Advance group name will be visible to Partners."
                        required
                        className={styles.advanceGroupName}
                        data-testid="advanceGroupFormName"
                      />

                      {mode === AdvanceGroupForm.MODE.CREATE && (
                        <Field
                          name="accountId"
                          label="Advance Account"
                          component={TextField}
                          disabled={isSubmitting}
                          required
                          data-testid="advanceGroupFormAdvanceAccount"
                        />
                      )}

                      {mode === AdvanceGroupForm.MODE.EDIT && (
                        <Stack gap={10}>
                          <Text>Advance Account</Text>
                          <Text className={styles.contractId}>{values.accountId}</Text>
                        </Stack>
                      )}

                      {mode === AdvanceGroupForm.MODE.CREATE && !initialContract && (
                        <Field
                          name="contract"
                          label="Contract"
                          component={ContractTypeAhead}
                          disabled={isSubmitting}
                          required
                          data-testid="advanceGroupFormContract"
                          handleCallback={contractChangeCallback}
                          contractName={values.contract.name}
                        />
                      )}

                      {(mode === AdvanceGroupForm.MODE.EDIT || initialContract) && (
                        <Stack gap={10}>
                          <Text>Contract</Text>
                          <Text className={styles.contractId}>{values.contract.name}</Text>
                        </Stack>
                      )}

                      {values.contract && values.contract.contractId && (
                        <Stack gap={10}>
                          <Text>Contract ID</Text>
                          <Text className={styles.contractId}>{values.contract.contractId}</Text>
                        </Stack>
                      )}
                    </StackGrid>
                  </SubModuleContent>
                </SubModule>

                <SubModule disableAccordian data-testid="advanceGroupGroupAssignmentSection">
                  <SubModuleHeader className={styles.assignHeaderWrapper} variant="large">
                    <span className={styles.assignHeader}>Group Assignment</span>
                    <span className={styles.editAssignment}>
                      <TextButton
                        data-testid="advanceGroupFormEditGroupAssignment"
                        type="button"
                        onClick={openPanel}
                      >
                        Edit
                      </TextButton>
                    </span>
                  </SubModuleHeader>

                  <SubModuleContent>
                    <AdvanceGroupAssignmentDetails groupAssignments={values.groupAssignments} />
                  </SubModuleContent>
                </SubModule>
              </ModuleContent>
            </Module>

            <Panel
              isOpen={isOpen}
              onDismiss={dismissPanel}
              type={PanelType.smallFluid}
              closeButtonAriaLabel="Close"
              headerText="Group Assignment"
              className={styles.panelHeading}
              isFooterAtBottom
            >
              <GroupAssignmentsForm
                advanceGroup={values}
                onSubmit={handleSaveGroupAssignment}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                onCancel={dismissPanel}
              />
            </Panel>
          </Form>
        )}
      </Formik>
    );
  },
);

AdvanceGroupForm.MODE = {
  CREATE: 'CREATE',
  EDIT: 'EDIT',
};

AdvanceGroupForm.propTypes = {
  advanceGroupId: PropTypes.string,
  onSubmit: PropTypes.func,
  onSave: PropTypes.func,
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
  mode: PropTypes.oneOf(Object.values(AdvanceGroupForm.MODE)),
  initialContract: PropTypes.object,
  advanceGroupToEdit: PropTypes.object,
};

AdvanceGroupForm.defaultProps = {
  advanceGroupId: null,
  onSubmit: null,
  onSave: null,
  mode: AdvanceGroupForm.MODE.CREATE,
  onSuccess: () => {},
  onFailure: () => {},
  initialContract: null,
  advanceGroupToEdit: null,
};

export default AdvanceGroupForm;
